import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router';
import type { DiscussionDetail } from '@contracts/discussions';
import { useDiscussion, useDiscussionActions } from '../api/queries';
import { BackLink, Broken, Button, Card, Empty, Pill, SkeletonRows, useNotices } from '../ui';
import { dayLabel, duration, since } from '../lib/time';
import { KindPill } from './Knowledge';
import { NoteText } from '../editor/NoteText';

/**
 * A topic (PRD 30 §3): the record a company would keep of a discussion that
 * ran over several meetings — what was decided, what was rejected and why,
 * what is still open — with the meetings it came from and the statements it
 * produced. An ADR that wrote itself from the meeting where the decision was
 * actually made.
 */
export function DiscussionPage(): ReactElement {
  const { id = '' } = useParams();
  const q = useDiscussion(id);
  return (
    <div className="page stack-lg">
      {q.isPending ? (
        <SkeletonRows rows={6} />
      ) : q.isError ? (
        <Card flush>
          {q.error.message.includes('404') ? (
            <Empty tone="none" lead="No such discussion — or not one you can open." />
          ) : (
            <Broken what="The discussion" retry={() => void q.refetch()} />
          )}
        </Card>
      ) : (
        <Loaded t={q.data} />
      )}
    </div>
  );
}

function Loaded({ t }: { t: DiscussionDetail }): ReactElement {
  const { writeUp } = useDiscussionActions();
  const { show } = useNotices();
  return (
    <>
      <div className="toolbar">
        <BackLink to="/knowledge">Knowledge</BackLink>
        <span className="crumb">
          Discussion · {dayLabel(t.startedAt)} – {dayLabel(t.endedAt)}
        </span>
        <div className="actions">
          {t.canAct && t.status === 'proposed' ? (
            <Button
              variant="primary"
              working={writeUp.isPending ? 'Queuing…' : false}
              onClick={() =>
                writeUp.mutate(t.id, {
                  onSuccess: () => show({ tone: 'success', text: 'Writing it up.' }),
                  onError: (err) => show({ tone: 'failure', text: err.message }),
                })
              }
            >
              Write it up
            </Button>
          ) : null}
        </div>
      </div>
      <div className="stack" style={{ gap: 4 }}>
        <h1 className="t-title" style={{ textWrap: 'balance' }}>
          {t.title}
        </h1>
        <p className="mono cluster" style={{ gap: 10, flexWrap: 'wrap' }}>
          <span>
            {t.meetings.length} meetings · {duration(t.seconds)}
          </span>
          {t.status === 'open' ? <Pill tone="amber">writing up…</Pill> : null}
          {t.status === 'proposed' ? <Pill>proposed</Pill> : null}
          {t.wroteUpAt ? <span>written up {since(t.wroteUpAt)}</span> : null}
        </p>
      </div>

      <div className="grid-2 aside">
        <div className="stack-lg">
          <Card title="The record">
            {t.writeUp ? (
              <div className="note" style={{ maxWidth: '68ch' }}>
                <NoteText>{t.writeUp}</NoteText>
              </div>
            ) : t.status === 'open' ? (
              <p className="t-secondary">
                Being written — one read across every meeting. A minute or two.
              </p>
            ) : (
              <p className="t-secondary">Not written up yet.</p>
            )}
          </Card>
          {t.entries.length > 0 ? (
            <Card title="What the company knows from it" flush>
              <div className="know-list">
                {t.entries.map((e) => (
                  <div key={e.id} className="know-row">
                    <KindPill kind={e.kind} />
                    <span className="statement">{e.statement}</span>
                    <Pill tone={e.status === 'current' ? 'green' : undefined}>{e.status}</Pill>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}
        </div>
        <Card title="The meetings" flush>
          <div className="rows">
            {t.meetings.map((m) => (
              <Link key={m.id} to={`/m/${m.id}`} className="row">
                <span className="row-main">
                  <span className="row-title">{m.title}</span>
                  <span className="row-sub">
                    <span className="mono">
                      {dayLabel(m.startedAt)} · {duration(m.seconds)}
                    </span>
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
